import { ArrowRight, CheckCircle, Building2, Handshake, Monitor, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Eyebrow } from './MoanaHero';
import { scrollTo } from './moana-data';

const MODES = [
  {
    id: 'delegation',
    icon: Building2,
    tier: 'Mode 1',
    name: 'Délégation totale',
    subtitle: 'AERH gère tout pour vous',
    desc: 'Vous nous confiez l\'intégralité de votre paie. AERH produit les bulletins, les déclarations sociales, le reporting et gère les situations sensibles. Vous ne touchez à rien.',
    who: 'TPE/PME sans service RH interne, gérants qui veulent se concentrer sur leur cœur de métier.',
    color: '#0F766E',
    tarif: 'Tarifs publiés ci-dessus',
    tarifSub: '850 à 1 100 F HT/salarié/mois selon effectif',
    available: true,
    features: [
      { text: 'Production des bulletins par AERH', ok: true },
      { text: 'Déclarations MO + CST par AERH', ok: true },
      { text: 'Reporting complet (livre, OD, registre)', ok: true },
      { text: 'Support RH dédié', ok: true },
      { text: 'Aucun outil à maîtriser côté client', ok: true },
      { text: 'Coffre-fort salariés (Pack Confort+)', ok: true },
    ],
    cta: 'Voir les packs Délégation',
    ctaAction: () => scrollTo('packs'),
  },
  {
    id: 'semi',
    icon: Handshake,
    tier: 'Mode 2',
    name: 'Semi-délégation',
    subtitle: 'Vous saisissez, AERH contrôle et valide',
    desc: 'Vous saisissez les variables de paie dans Moana Paye (heures, absences, primes). AERH contrôle, corrige si nécessaire, valide et produit les bulletins et déclarations.',
    who: 'PME avec un(e) assistant(e) RH ou comptable qui souhaite garder la main sur la saisie tout en bénéficiant du contrôle d\'un expert paie.',
    color: '#D97706',
    tarif: 'Tarif sur mesure',
    tarifSub: 'Nous consulter pour un devis personnalisé',
    available: false,
    features: [
      { text: 'Accès au logiciel Moana Paye', ok: true },
      { text: 'Saisie des variables par le client', ok: true },
      { text: 'Contrôle et validation par AERH', ok: true },
      { text: 'Production bulletins par AERH', ok: true },
      { text: 'Déclarations MO + CST par AERH', ok: true },
      { text: 'Formation initiale à l\'outil incluse', ok: true },
    ],
    cta: 'Demander un devis',
    ctaAction: () => scrollTo('contact-moana'),
  },
  {
    id: 'autonome',
    icon: Monitor,
    tier: 'Mode 3',
    name: 'Autonomie totale',
    subtitle: 'Vous gérez votre paie avec Moana Paye',
    desc: 'Vous utilisez Moana Paye en totale autonomie : saisie, calcul, édition des bulletins, déclarations. AERH fournit le logiciel, les mises à jour réglementaires et le support technique.',
    who: 'Entreprises dotées d\'un service RH/paie interne compétent, souhaitant un outil 100 % polynésien sans dépendre d\'un prestataire pour la production.',
    color: '#6366F1',
    tarif: 'Tarif sur mesure',
    tarifSub: 'Licence logiciel — nous consulter',
    available: false,
    features: [
      { text: 'Accès complet au logiciel Moana Paye', ok: true },
      { text: 'Saisie, calcul et édition en autonomie', ok: true },
      { text: 'Mises à jour réglementaires automatiques', ok: true },
      { text: 'Support technique inclus', ok: true },
      { text: 'Espace salariés en ligne', ok: true },
      { text: 'Formation initiale à l\'outil incluse', ok: true },
    ],
    cta: 'Demander un devis',
    ctaAction: () => scrollTo('contact-moana'),
  },
];

export const MoanaModes = () => (
  <section id="modes" className="py-20 bg-white" data-testid="modes-section">
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10">
      <div className="mb-12">
        <Eyebrow>3 modes d'utilisation</Eyebrow>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937]">
          Vous choisissez <em className="italic text-[#F59E0B]">votre niveau d'implication</em>
        </h2>
        <p className="text-sm text-gray-500 leading-relaxed mt-3 max-w-[620px]">
          Que vous souhaitiez tout déléguer, collaborer avec nos équipes ou gérer votre paie en autonomie, Moana Paye s'adapte à votre organisation.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {MODES.map(mode => (
          <div key={mode.id}
            className={`bg-white border rounded-xl overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-lg transition-all ${
              mode.id === 'delegation' ? 'border-[#F59E0B] ring-2 ring-[#F59E0B]/20' : 'border-gray-200'
            }`}
            data-testid={`mode-${mode.id}`}
          >
            {/* Header */}
            <div className="p-7 relative" style={{ backgroundColor: mode.color }}>
              {mode.id === 'delegation' && (
                <span className="absolute top-3.5 right-3.5 bg-[#F59E0B] text-[#0F766E] text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded">
                  Le + demandé
                </span>
              )}
              <mode.icon className="w-8 h-8 text-white/60 mb-3" />
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#F59E0B] mb-1">{mode.tier}</p>
              <h3 className="text-2xl font-bold text-white mb-1">{mode.name}</h3>
              <p className="text-xs text-white/55 italic">{mode.subtitle}</p>
              <div className="mt-4 pt-3.5 border-t border-white/10">
                <p className="text-lg font-bold text-white">{mode.tarif}</p>
                <p className="text-xs text-white/40 mt-0.5">{mode.tarifSub}</p>
              </div>
            </div>

            {/* Body */}
            <div className="p-7 flex-1 flex flex-col">
              <p className="text-sm text-gray-500 leading-relaxed mb-4">{mode.desc}</p>

              <ul className="space-y-0 mb-4 flex-1">
                {mode.features.map((f, i) => (
                  <li key={i} className="flex gap-2.5 text-sm py-2 border-b border-gray-100 last:border-0 items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{f.text}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 italic leading-relaxed mb-5">
                <strong className="not-italic text-[#1F2937]">Idéal :</strong> {mode.who}
              </div>

              <Button
                onClick={mode.ctaAction}
                className={`w-full font-semibold py-5 ${
                  mode.available
                    ? 'bg-[#F59E0B] hover:bg-[#D97706] text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                data-testid={`mode-cta-${mode.id}`}
              >
                {mode.cta} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center">
        <p className="text-sm text-gray-400">
          Vous hésitez entre deux modes ? Contactez-nous pour un audit gratuit — nous vous conseillerons le mode le mieux adapté à votre structure.
        </p>
      </div>
    </div>
  </section>
);
